import {environment} from '../../environments/environment';
import {inject, Injectable} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {NgxPermissionsService} from 'ngx-permissions';
import {firstValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';
import {NotificationService} from './notification.service';
import {MatDialog} from '@angular/material/dialog';
import {AuthTokenModel, BaseResponse, ResponseObject} from '../common/interfaces/global-interfaces';
import {StorageService} from './storage.service';
import {Router} from '@angular/router';
import {GraphqlService} from './graphql.service';
import * as fromAuthUserGql from '../store/entities/uaa/auth-user.graphql';
import {UserTypeEnum} from '../store/entities/uaa/auth-user.model';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {AuthUserActions} from '../store/entities/uaa/auth-user.actions';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
 
  constructor(
    private store: Store<AppState>,
    private http: HttpClient,
    private permissionsService: NgxPermissionsService,
    private storageService: StorageService,
    public dialog: MatDialog,
    private notificationService: NotificationService,
    private router: Router,
    private apollo: GraphqlService
  ) {
  }

  async login(formData: any): Promise<AuthTokenModel> {
    const body = `username=${encodeURIComponent(
        formData.username
    )}&password=${encodeURIComponent(
        formData.password
    )}&grant_type=${encodeURIComponent(
        environment.GRANT_TYPE
    )}&client_id=${encodeURIComponent(
        environment.CLIENT_ID
    )}&client_secret=${encodeURIComponent(
        environment.CLIENT_SECRET
    )}
        `;

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    });

    try {
      return await firstValueFrom(
        this.http.post<AuthTokenModel>(environment.AUTH_SERVER_URL + '/oauth2/access_token', body, {headers})
          .pipe(map((tokenDetails) => {
            if (tokenDetails && tokenDetails.access_token) {
              // store user details and jwt token in local storage to keep user logged in between page refreshes
              this.storageService.set('currentClient', tokenDetails.access_token);
              this.storageService.set('refreshToken', tokenDetails?.refresh_token);
              this.storageService.set('expireTime', tokenDetails?.expires_in);
              const expiry = Math.round(Date.now()) + (tokenDetails?.expires_in * 1000); // time valid token in ISO-8601
              this.storageService.set('expiry', expiry);
              this.storageService.set('userProfileDetails', tokenDetails?.user);
              // this.storageService.set('warehouseId', tokenDetails?.user?.warehouse_unique_id)
              // this.storageService.set('userType', tokenDetails?.user?.account_type);
            }
            return tokenDetails;
          }))
      );
    } catch (e) {
      if (e?.error?.error?.includes('invalid_grant')) {
        this.notificationService.errorSnackbar('Username or password is incorrect');
      }
    }
    return undefined;
  }

  async refreshToken(): Promise<AuthTokenModel> {
    const token = this.storageService.get('currentClient');
    const refresh = this.storageService.get('refreshToken');
    if (token && refresh) {
      const body = `refresh_token=${encodeURIComponent(refresh)}&grant_type=${encodeURIComponent('refresh_token')}`;

      const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
        Authorization: 'Basic ' + btoa(environment.CLIENT_ID + ':' + environment.CLIENT_SECRET),
      });

      try {
        return await firstValueFrom(
          this.http.post<AuthTokenModel>(environment.AUTH_SERVER_URL + '/oauth2/access_token', body, {headers})
            .pipe(map((tokenDetails) => {
              if (tokenDetails && tokenDetails.access_token) {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                this.storageService.set('currentClient', tokenDetails.access_token);
                this.storageService.set('refreshToken', tokenDetails?.refresh_token);
                this.storageService.set('expireTime', tokenDetails?.expires_in);
                const expiry = Math.round(Date.now()) + (tokenDetails?.expires_in * 1000); // time valid token in ISO-8601
                this.storageService.set('expiry', expiry);
                // this.storageService.set('userType', tokenDetails?.user?.user_type);
                this.monitorRefreshToken();
              }
              return tokenDetails;
            }))
        );
      } catch (e) {
        console.error('Failed to get refresh token', e);
      }
    }
    return undefined;
  }

  monitorRefreshToken() {
    const expiry: number = this.storageService.get('expiry');
    const refreshTime = expiry - Date.now();
    if (expiry && refreshTime > 10000) {
      console.log('refresh after minutes', refreshTime / 60000);
      const timeOut = setTimeout(() => {
        clearInterval(timeOut);
        if (this.alreadyLoggedIn()) {
          this.refreshToken();
        }
      }, refreshTime - 10000);
    }
  }

  logout(): any {
    this.clearStore();
    this.dialog.closeAll()
    this.router.navigate(['', 'guest-landing']).then();
  }

  async resetPassword(formData: any): Promise<ResponseObject> {
    try {
      const response: any = await this.apollo.mutate({
        mutation: fromAuthUserGql.RESET_PASSWORD,
        variables: {
          input: formData
        },
      });
      const result: BaseResponse<any> = response?.data?.activateOrResetPasswordMutation;
      return result?.response;
    } catch (e) {
      this.notificationService.errorSnackbar('An error occured please try again later');
      return null;
    }
  }

  async forgotPassword(formData: any): Promise<any> {
    try {
      const response: any = await this.apollo.mutate({
        mutation: fromAuthUserGql.GET_RESET_LINK,
        variables: {
          email: formData.email,
        },
      });
      const result: BaseResponse<any> = response?.data?.forgotPasswordMutation;
      if (result?.response?.code === 9001) {
        this.notificationService.successSnackbar(
          'Password reset link sent successfully!'
        );
      } else {
        throw Error(result?.response?.message);
      }
    } catch (e) {
      this.notificationService.errorSnackbar(
        'An error occured please try again later'
      );
      this.notificationService.errorSnackbar('An error occured: ' + e.message);
    }
  }

  alreadyLoggedIn(): boolean {
    return !!this.storageService.get('currentClient');
  }

  isStaffAccess(): boolean {
    return [UserTypeEnum.INTERNAL, UserTypeEnum.ADMIN].indexOf(this.storageService.get('userType')) > -1;
  }

  isAdminAccess(): boolean {
    return this.storageService.get('userType') === UserTypeEnum.ADMIN;
  }

  isPortalAccess(): boolean {
    return this.storageService.get('userType') === UserTypeEnum.COMPLAINANT;
  }

  clearStore() {
    this.store.dispatch(AuthUserActions.logout());
    this.storageService.clear();
    localStorage.clear();
    window.sessionStorage.clear();
    sessionStorage.clear();
    this.permissionsService.flushPermissions();
  }

  collectFailedRequest(err: any): void {
    let errorCode = err.status;
    let errorDescription = err.reason;
    let message = err?.reason !== null ? err?.reason : err?.message;

    if (errorCode === 401) {
      this.clearStore();
      this.router.navigateByUrl('/login').then();

      if (errorDescription === null) {
        message =
          'You Dont Have Right to Access this Application, Please Contact Support Team for More Details';
      }
    }
    if (errorCode === 404) {
      message = 'Service Temporarily Unavailable';
    }
    if (errorCode === 500) {
      message = null;
      // message = 'Something went wrong in server side.';
      console.error('Something went wrong in server side.');
      this.storageService.clear();
    }
    if (errorCode === 504) {
      message = 'Service Temporarily Unavailable';
    }

    if (message !== null) {
      // this.notificationService.errorMessage(message)
    }
  }

}
